/**
 * The speaker end of `SessionEvents.onSound` (IC-7b).
 *
 * The rules decide **what** plays — `SoundService.Play(SoundTypeEnum)` in the
 * original, `events.onSound` here — and this turns that into a noise. It lives
 * under `render/` rather than `game/` for the same reason the meshes do: a rule
 * must be runnable with no browser at all, and `npm test` has no
 * `AudioContext`.
 *
 * 🔍 **Volume 1, pitch 1, no loop — read, not guessed.** All three public doors
 * into `SoundService` hard-code the numbers into the call:
 *
 *     SoundService$$Play:16         ExecutePlay(0x3f800000, 0x3f800000, 0, svc, type, 0)
 *     SoundService$$PlayLoop        ExecutePlay(0x3f800000, 0x3f800000, 0, svc, type, 1)
 *     SoundService$$PlayOneShot     ExecutePlayOneShot(0x3f800000, 0x3f800000, 0, svc, type)
 *
 * float `0x3f800000` is 1.0 (and this file's neighbour in `session.ts` is a
 * reminder to check the width before reading a hex constant). The last argument
 * is the one `ExecutePlay:81` hands to `AudioSource.set_loop`, so looping is a
 * property of *which door was used*, not of the clip — and the gameplay scene
 * only ever calls `Play`.
 *
 * ⚠️ **A previous version of this note said `SoundData` "carried a volume, a
 * pitch and a loop flag per clip" and that they were lost with the stripped
 * asset. That is wrong.** `dump.cs`:712685 — `public struct SoundData { public
 * SoundTypeEnum type; public AudioClip clip; }`. Two fields. There was never a
 * per-clip volume to lose; the stub cost the port the **enum → file** mapping
 * and nothing else, and `soundLibrary.ts` recovered that separately.
 *
 * The `SoundBuilder` fluent API can override all three (`WithVolume` → 0xc,
 * `WithPitch` → 0x10, `WithDelay` → 0x14, defaults set by `SoundService.Build`),
 * but its only three call sites in the build are `LevelDifficultyPopupAnimations`,
 * `FeatureUnlockAnimations` and `SuccessAnimations` — all popups, none in the
 * gameplay level.
 *
 * ❓ Not ported at all: `Taptic.Heavy()`, which the blocked-click branch calls
 * alongside `Play(10)`. Left out rather than approximated — a phone vibration
 * has no browser equivalent worth inventing.
 */
import { SOUND_CLIPS } from '../game/soundLibrary'
import type { SoundTypeEnum } from '../core/enums'

/** 🔍 `SoundService.Play` passes float `0x3f800000` for both. See above. */
export const DEFAULT_VOLUME = 1
export const DEFAULT_PITCH = 1

/**
 * How many `Audio` elements are kept per clip.
 *
 * This is a **pool size, not a rule**. The original pools `AudioSource`s the
 * same way (`_pool`, `GetFromPool`, `CreateNewSource`) and grows the pool when
 * it runs dry, so a fixed handful here is an implementation detail of the
 * speaker, not a decision about what is allowed to play.
 *
 * ⚠️ It used to be described as "how many copies of one clip may overlap" — and
 * that was the port's throttle. It is not the original's throttle; see
 * `SameSoundCooldown` below.
 */
const VOICES = 4

/**
 * 🔍 `SoundService.CanPlaySound` (`__0x20db0e0.c`).
 *
 * ```
 * if (_enableSameSoundCooldown) {                                    // 0x24
 *     if (_lastPlayedTimes.TryGetValue(type, out t)                  // 0x60
 *         && Time.unscaledTime - t < _soundCooldownDuration)         // 0x28
 *         return false;
 *     _lastPlayedTimes[type] = Time.unscaledTime;
 *     return true;
 * }
 * ```
 *
 * The gate is keyed by **`SoundTypeEnum`**, not by clip file, and asking is
 * what stamps the clock — a refused sound does **not** push the next one
 * further away. Checked in all three entry points (`SoundBuilder.Play`,
 * `PlayLoop`, `PlayOneShot`), always after `IsSoundEnabled`, so a muted game
 * stamps nothing.
 *
 * It runs once per played sound, not twice: `ExecutePlay` carries the same
 * check, but the compiler **inlined** `ExecutePlay` into `SoundBuilder.Play`
 * (four `SoundService__ExecutePlay_plus_` labels sit in the builder's body), so
 * the two checks are one.
 */
export class SameSoundCooldown {
  private readonly lastPlayed = new Map<SoundTypeEnum, number>()

  constructor(
    /** Seconds a type must wait before it may sound again. */
    private readonly duration: number,
    /** `Time.unscaledTime`, in seconds. */
    private readonly now: () => number,
    /** `_enableSameSoundCooldown` — when false the gate always allows. */
    private readonly enabled = true,
  ) {}

  /** Returns whether the sound may play, stamping the clock when it may. */
  allow(sound: SoundTypeEnum): boolean {
    if (!this.enabled) return true
    const now = this.now()
    const last = this.lastPlayed.get(sound)
    if (last !== undefined && now - last < this.duration) return false
    this.lastPlayed.set(sound, now)
    return true
  }
}

/**
 * ❓ **PLACEHOLDER — `_soundCooldownDuration`, and the flag beside it.**
 *
 * Both are `readonly` fields set from the constructor, and the construction
 * site is `Bootstrapper.<InitializeServices>d__12$$MoveNext`:158, where Ghidra
 * dropped **every** argument (`SoundService___ctor(uVar10)`); the argument it
 * did keep is read out of a config object (`*(lVar7 + 0x20) + 0x40`), not a
 * literal. Inside the constructor, `0x28` is assembled from `in_b0` — a float
 * in a register the decompiler could not name.
 *
 * To pin it: the original bundle, or a decompiler that keeps constructor
 * arguments. Until then this number is **chosen**, not read.
 *
 * `_enableSameSoundCooldown` is unreadable for the same reason and is defaulted
 * to **on** — porting the mechanism switched off would be porting nothing. That
 * is a judgement call and it is logged in checklist section 10 as one.
 */
export const SAME_SOUND_COOLDOWN_SECONDS = 0.05

/**
 * ⚠️ **Not ported — a browser rule, not a game rule.** The original gets this
 * from its `AVAudioSession` category; a web page has to ask.
 *
 * On iOS the Ring/Silent switch does not silence a web page by itself. WebKit
 * picks the session category, and the default (`'auto'`) resolves to a
 * *playback* category once anything plays — the category a music app uses,
 * which by design ignores the switch. `'ambient'` is the category for audio the
 * app does not need: it mixes with other audio and **is** silenced by the
 * switch, in both directions, with no polling and nothing for the page to do.
 *
 * `navigator.audioSession` is WebKit-only (Safari 16.4+) and simply absent
 * elsewhere, where the platform already routes page audio through the media
 * volume the phone's own controls change. ❓ iOS below 16.4 has no lever at all;
 * left unhandled rather than approximated.
 *
 * ⚠️ This file has a **second copy** in `playable-ads/src/render/`, which is the
 * build an ad network reviews on a real iPhone — that is where the finding came
 * from. `web/tests/audioSession.test.ts` checks both, because a check that opens
 * one file says nothing about the other (see CLAUDE.md section 8).
 */
export function configureAudioSession(): void {
  if (typeof navigator === 'undefined') return
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession
  if (!session) return
  try {
    session.type = 'ambient'
  } catch {
    // A frozen object, or a type this WebKit does not know. Not worth throwing
    // out of a constructor over.
  }
}

export interface SoundPlayerOptions {
  /** Where the `.ogg` files live, relative to the page. */
  base?: string
  /** Off by default so a page can start silent and be switched on by a gesture. */
  enabled?: boolean
  /** Overrides the ❓ placeholder threshold; mainly so tests can be explicit. */
  cooldownSeconds?: number
  /** `Time.unscaledTime` in seconds. Injected so tests need no wall clock. */
  now?: () => number
}

/**
 * Plays clips through pooled `Audio` elements.
 *
 * Constructing this does **not** touch the network: elements are created for a
 * clip the first time it is asked for, so a level that never breaks ice never
 * fetches `IceBreak.ogg`.
 */
export class SoundPlayer {
  private readonly base: string
  private readonly voices = new Map<string, HTMLAudioElement[]>()
  private next = new Map<string, number>()
  private readonly cooldown: SameSoundCooldown
  private armed: boolean

  constructor(options: SoundPlayerOptions = {}) {
    this.base = options.base ?? 'assets/audio/'
    this.armed = options.enabled ?? false
    this.cooldown = new SameSoundCooldown(
      options.cooldownSeconds ?? SAME_SOUND_COOLDOWN_SECONDS,
      options.now ?? (() => performance.now() / 1000),
    )
    configureAudioSession()
  }

  /** `SettingsModel.IsSoundEnabled` — the sound button in the settings panel. */
  get enabled(): boolean {
    return this.armed
  }

  /**
   * Switching on is the last moment before a sound can start, so the session
   * type is re-declared here as well as in the constructor: a WebView that only
   * exposes `audioSession` after its first gesture still ends up ambient.
   */
  set enabled(on: boolean) {
    if (on && !this.armed) configureAudioSession()
    this.armed = on
  }

  /**
   * Hand this to `new GameSession(level, { events: { onSound: player.play } })`.
   *
   * Bound so it can be passed as a bare function, which is how the events
   * interface takes it.
   */
  readonly play = (sound: SoundTypeEnum): void => {
    // Order matters and is the original's: `IsSoundEnabled` first, then the
    // cooldown — so a muted game leaves the clock unstamped and the first sound
    // after unmuting is never swallowed.
    if (!this.enabled) return
    this.playUnconditionally(sound)
  }

  /**
   * `SoundService.ExecutePlay` — the entry below the `IsSoundEnabled` gate.
   * `SettingsModel.OnSoundButtonClicked` calls it directly, so the switch that
   * turns sound off still clicks. Not for anything else.
   */
  playUnconditionally(sound: SoundTypeEnum): void {
    if (!this.cooldown.allow(sound)) return
    const clip = SOUND_CLIPS[sound]
    if (!clip) return

    let pool = this.voices.get(clip)
    if (!pool) {
      pool = []
      for (let i = 0; i < VOICES; i++) {
        const element = new Audio(`${this.base}${clip}.ogg`)
        element.volume = DEFAULT_VOLUME
        element.preload = 'auto'
        pool.push(element)
      }
      this.voices.set(clip, pool)
      this.next.set(clip, 0)
    }

    const at = this.next.get(clip) ?? 0
    this.next.set(clip, (at + 1) % pool.length)
    const element = pool[at]
    // Rewinding is what makes a re-trigger audible: an element already playing
    // ignores `play()` and would otherwise drop the second chip entirely.
    element.currentTime = 0
    // A page that has not been interacted with rejects playback; that is the
    // browser's rule, not an error worth surfacing.
    void element.play().catch(() => {})
  }
}
